<template>
  <layout-page>

    <page-breadcrumb :hierarchy="breadCrumbs" />

    <div
        class="mx-auto w-full justify-center px-0 lg:px-0 lg:container pb-4"
        ref="searchBar"
    >
      <SearchBar
          page-type="home"
          :job_type="parameters.job_types && parameters.job_types[0]"
          :industry="parameters.industry"
          :location="currentLocationOption"
          :skill="parameters.skill"
          :skill-options="getSearchbarSkillOptions(parameters.industry)"
          :emp_cand="parameters.emp_cand"
      />
    </div>

    <template v-if="allLocationsInfoLoaded">
      <article class="mb-6 w-full px-4 lg:container">
        <h1 class="mb-3 text-3xl font-bold text-blue">
          Working with local Recruitment Agencies
        </h1>
        <p class="mb-2 text-grey-70">
          With modern methods of communication available, there is nothing to
          stop you working with a recruitment agency located anywhere in the
          country - but there can still be many benefits to using a more local
          service. Firstly, an agency which recruits locally is potentially more
          likely to have met with a candidate or employer in person - meaning
          that the matchmaking process they go through may be able to operate on
          a more subtle level than if a face to face meeting had not taken
          place.
        </p>
        <p class="mb-2 text-grey-70">
          Secondly, some recruiters work specifically within a particular field
          of employment - and these specialists often tend to be based in
          geographic areas where that field is popular. Many specialist
          engineering recruiters can be found near major industrial activity for
          example. This can mean that these very focussed agencies have an
          advantage in certain situations.
        </p>
        <p class="text-grey-70">
          On this page you can find recruitment agencies in the UK sorted into
          popular geographic areas. If you can't find the location you are
          looking for, please click the link at the bottom of the page - which
          details agencies located in further areas.
        </p>
      </article>
      <article class="mb-6 w-full px-4 lg:container">
        <!-- <h1 class="sr-only">Recruitment Agencies By County</h1> -->
        <div class="columns-1 md:columns-3">
          <div
            v-for="county in getCountiesWithTowns"
            :key="county.countyUrlCode"
            class="mb-6 break-inside-avoid-column"
          >
            <h2>
              <static-search-link
                class="text-2xl font-bold leading-8 text-blue-60"
                :to="county.searchUrl"
              >
                {{ county.label }}
              </static-search-link>
            </h2>
            <ul>
              <li v-for="town in county.towns" :key="town.townUrlCode">
                <static-search-link
                  class="pl-4 text-xl leading-10 text-blue-30"
                  :to="town.searchUrl"
                >
                  {{ town.label }}
                </static-search-link>
              </li>
            </ul>
          </div>
        </div>
      </article>
      <article class="w-full px-4 lg:container">
        <h1 class="mb-3 text-3xl font-bold text-blue">
          Recruitment Agencies By Region
        </h1>
        <div class="flex w-full flex-col justify-between md:flex-row">
          <ul>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/NW.htm"
                >North West England
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/NE.htm"
                >North East England
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/SW.htm"
                >South West England
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/SE.htm"
                >South East England
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/MID.htm"
                >Midlands
              </static-search-link>
            </li>
          </ul>
          <ul>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/EA.htm"
                >East Anglia
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/LON.htm"
                >London
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/HC.htm"
                >Home Counties
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/WB.htm"
                >Welsh Borders
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/YH.htm"
                >Yorkshire &amp; Humberside
              </static-search-link>
            </li>
          </ul>
          <ul>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/SCO.htm"
                >Scotland
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/NI.htm"
                >Northern Ireland
              </static-search-link>
            </li>
            <li>
              <static-search-link
                class="pl-4 text-xl leading-10 text-blue-30"
                to="/agencysearch/byregion/WAL.htm"
                >Wales
              </static-search-link>
            </li>
          </ul>
        </div>
      </article>
    </template>

    <Loading v-else />

  </layout-page>
</template>

<script>
import PageBreadcrumb from 'Components/PageBreadcrumb';
import Loading from 'Components/Loading.vue';
import SearchBar from 'Components/SearchBar';
import layoutPage from 'Components/layouts/LayoutPage';
import IconSpinner from 'Components/Icons/IconSpinner';
import StaticSearchLink from 'Components/StaticSearchLink';
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  name: 'AllLocations',
  components: {
    PageBreadcrumb,
    SearchBar,
    StaticSearchLink,
    IconSpinner,
    layoutPage,
    Loading,
  },
  methods: {
    ...mapActions('info', ['loadInfo']),
    ...mapActions('search', ['resetState']),
    ...mapActions('location', ['getAllLocationsInfo']),
  },
  computed: {
    ...mapState('location', ['allLocationsInfoLoaded']),
    ...mapState('search', [
        'parameters'
    ]),
    ...mapGetters('location', ['getCountiesWithTowns']),
    ...mapGetters('search', ['currentLocationOption']),
    ...mapGetters('industry', ['getSearchbarSkillOptions']),
    breadCrumbs : function(){
      return [
        {
          name: 'Find recruitment agencies based on their location'
        }
      ]
    }
  },
  mounted() {
    if (!this.allLocationsInfoLoaded) {
      this.getAllLocationsInfo();
    }
    if (!this.loaded) {
      this.loadInfo();
    }
    // Reset the search state on homepage load
    this.resetState();
  },
};
</script>
